import { createTheme } from "@mui/material"
import { grey } from "@mui/material/colors"
import defaultTheme from './default'
import { ExpandMore } from '@mui/icons-material'
import defaultDarkTheme from './defaultDarkTheme'

function getFullscreenElement () {
    if ( typeof document === "undefined" ) return null
    // Standard + vendor-prefixed fallbacks
    return (
        document.fullscreenElement ??
        ( document ).webkitFullscreenElement ??
        ( document ).mozFullScreenElement ??
        ( document ).msFullscreenElement ??
        null
    )
}

const darkTheme = createTheme( {
    theme: "dark",
    border: [
        "none",
        '1px solid #27272A',
    ],
    table: {
        headerBg: "#404040",
        headerColor: defaultDarkTheme.palette.text.secondary,
        color: '#FFFFFF',
        fontSize: "14px"
    },
    zIndex: {
        modal: 3000,
        tooltip: 5000,
    },

    layout: {
        pagePadding: { md: "20px", xs: "10px" },
        borderRadius: "5px",
        cardsGap: "10px",
        sectionsGap: "20px",
    },

    palette: {
        mode: 'dark',
        borderColor: "#3E3E3E",
        contentBg: "#1E1E1E",
        contentBgDark: "#202020",
        labelColor: '#fafafa',
        form: {
            labelColor: '#fafafa',
            formBg: "#121212",
            formCardBg: "#000",
            placeholder: "#aaa",
            inputBg: "#27272a"
        },
        shadedText: "#eee",

        nav: {
            font: "#ffffff",
            sidebar: "#1E1E1E",
            sidebarHover: "#FFFFFF1F",
            activeSidebarItem: "#db8d06",
            selected: "#151D48"
        },

        defaultColor: "#fff",
        greyedOut: "#3e3e3e",
        common: {
            black: "#000000",
            white: "#ffffff",
            font: "#ffffff",
            bg: "#121212"
        },
        layoutBackground: "#1E1E1E",
        background: { paper: "#121212", default: "#121212" },
        cardBackground: "#121212",

        // Main colors
        primary: {
            main: '#1d4ed8',
            light: '#3f6be4',
            dark: '#0d37aa',
            contrastText: '#fff'
        },
        primaryDark: {
            main: '#eee',
            light: '#eee',
            dark: '#eee',
            contrastText: '#000'
        },
        errorMessage: {
            main: "#F44336",
            light: "#FF8D9D",
            dark: "#de5f71",
            contrastText: "#fff"
        },
        customThemeColor: {
            main: "#E25A26",
            light: "#d4653b",
            dark: "#d94c16",
            contrastText: "#fff"
        },
        warning: {
            main: "#ffc107",
            dark: "#b28704",
            light: "#ffcd38",
            contrastText: "#000000"
        },
        greyed: {
            main: grey[400],
            light: grey[200],
            dark: grey[600],
            contrastText: "#fff"
        },
        contrast: {
            main: "#FFFFFF",
            light: "#FEFEFE",
            dark: "#FAFAFA",
            contrastText: "#000000"
        },
        rowHover: "hsl(240 3.7% 15.9%)"
    },
    typography: {
        fontFamily: "'Roboto', sans-serif",
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 600,
        color: "#fff",
        h6: { color: "#fff" },
        h5: { color: "#fff" },
        h4: { color: "#fff" },
        h3: { color: "#fff" },
        h2: { color: "#fff" },
        h1: { color: "#fff" },
        subtitle2: { color: "#e0e0e0" },
        subtitle1: { color: "#fff" },
        body1: { color: "#fff" },
        body2: { color: "#fff" },
    },
    components: {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    h1: 'h1',
                    h2: 'h1',
                    h3: 'h1',
                    h4: 'h1',
                    h5: 'h1',
                    h6: 'h1',
                    subtitle1: 'h6',
                    subtitle2: 'span',
                    body1: 'p',
                    body2: 'p',
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: "10px",
                    backgroundImage: "none"
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: "5px",
                    border: "1px solid #27272a",
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    boxShadow: `0 10px 30px -10px rgba(0, 0, 0, 0.5), 0 4px 10px -5px rgba(0, 0, 0, 0.3)`,
                }
            }
        },
        MuiInputBase: {
            defaultProps: {
                color: "secondary",
            },
            styleOverrides: {
                root: {
                    background: "#121215",
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1976d2", // optional
                        borderWidth: "0px", // keep original border
                    },
                    "&.Mui-focused": {
                        boxShadow: "0 0 0 4px rgba(116, 116, 116, 0.4)", // glow effect outside
                    },
                },
                input: {
                    fontSize: "14px",
                    fontWeight: "300"
                }
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    "&.Mui-focused": {
                        boxShadow: "none", // glow effect outside
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: defaultDarkTheme.palette.divider, // Much darker on focus
                        borderWidth: '1px',   // Optional: make it slightly thicker
                    },
                }
            },
            defaultProps: {
                IconComponent: ExpandMore,
                MenuProps: {
                    anchorOrigin: {
                        vertical: "bottom", horizontal: "left"
                    },
                    transformOrigin: {
                        vertical: "top", horizontal: "left"
                    },
                    slotProps: {
                        paper: {
                            sx: { marginTop: "5px", bgcolor: "#1E1E1E" }
                        }
                    }
                }
            }
        },

        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    margin: 0,
                    marginTop: "3px",
                    color: "#F44336",
                }
            }
        },

        MuiTableCell: {
            styleOverrides: {
                root: {
                    border: "none",
                    fontSize: "14px"
                }
            }
        },
        MuiMenu: {
            defaultProps: {
                autoFocus: true,
                slotProps: {
                    paper: {
                        className: "slim-custom-scrollbar",
                        sx: {
                            bgcolor: "#1E1E1E"
                        }
                    }
                }
            },
            styleOverrides: {
                paper: {
                    padding: "0",
                    borderRadius: "5px",
                    maxHeight: "350px",
                    marginTop: "5px"
                }
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    margin: "5px",
                    borderRadius: "5px",
                    ":hover": {
                        background: "#db8d0644",
                        color: "white"
                    },
                    '&.Mui-selected:hover': {
                        background: "#db8d0666",
                        color: "#fff"
                    },
                }
            }
        },
        MuiList: {
            styleOverrides: {
                root: {
                    padding: 0,
                }
            }
        },
        MuiDialog: {
            defaultProps: {
                slotProps: {
                    paper: {
                        className: "custom-scrollbar",
                        sx: { width: { sm: "90%", xs: "100%" }, maxWidth: "1024px" }
                    }
                }
            },
            styleOverrides: {
                root: {
                    background: "rgba(222, 222, 222, 0.53)",
                    // backdropFilter: "blur(5px)",
                }
            }
        },
        MuiDrawer: {
            styleOverrides: {
                root: {
                    background: "rgba(125, 125, 125, 0.53)",
                }
            }
        },
        MuiTooltip: {
            defaultProps: {
                disableInteractive: true,
                slotProps: {
                    popper: {
                        container: ( anchorEl, a, b, c ) => {
                            const fs = getFullscreenElement()
                            // Prefer an HTMLElement; otherwise fall back to body
                            return ( fs instanceof HTMLElement ? fs : document.body )
                        },
                    }
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "capitalize",
                    // background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`
                }
            }
        },

        MuiSkeleton: {
            defaultProps: {
                animation: "wave"
            },
            styleOverrides: {
                root: {
                    transform: "scale(1)",
                    borderRadius: 10,
                }
            }
        },

        // CUSTOM COMPOENENTS FRO DATE AND TIME PICKERS

        MuiPickerOutlinedInput: {
            defaultProps: {
                slotProps: {
                    textField: {
                        size: "small",
                        slotProps: {
                            input: {
                                sx: {
                                    bgcolor: "#121215"
                                }
                            }
                        }
                    },
                    desktopPaper: {
                        sx: {
                            border: '1px solid #27272A',
                            marginY: "5px",
                            bgcolor: "#1E1E1E"
                        }
                    },
                    mobilePaper: {
                        sx: {
                            border: '1px solid #27272A',
                            marginY: "5px",
                            bgcolor: "#1E1E1E",
                            zIndex: defaultTheme.zIndex.tooltip * 4
                        }
                    },
                    popper: {
                        sx: {
                            margin: "10px",
                            zIndex: defaultTheme.zIndex.tooltip * 4
                        }
                    }
                }
            }
        },
    },

} )

export default darkTheme